import { authenticatedFetch } from '../utils/fetchUtils';

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '')) + '/api/compat';

// Name-prefix autocomplete — returns [{ userId, name, maskedEmail }] (active users).
export const suggestUsers = async (q) => {
  if (!q || q.trim().length < 2) return [];
  const response = await authenticatedFetch(`${BASE_URL}/users/suggest?q=${encodeURIComponent(q.trim())}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.suggestions || [];
};

// Match a picked suggestion by opaque userId — always an instant match.
export const matchUser = async (userId) => {
  const response = await authenticatedFetch(`${BASE_URL}/search`, {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Match failed.');
  return data;
};

// Exact-email submit — returns { status: 'complete', match } or { status: 'not_found', email }.
export const searchMatch = async (email) => {
  const response = await authenticatedFetch(`${BASE_URL}/search`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Search failed.');
  return data;
};

// Create a pending invite for a non-user email.
export const createInvite = async (email, name) => {
  const response = await authenticatedFetch(`${BASE_URL}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, name }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to send invite.');
  return data;
};

// Accept an invite by token (existing-user path, called after login).
export const acceptInvite = async (token) => {
  const response = await authenticatedFetch(`${BASE_URL}/accept-invite`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to accept invite.');
  return data.match;
};

export const getMatches = async () => {
  const response = await authenticatedFetch(`${BASE_URL}/matches`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.matches || [];
};

export const getMatch = async (matchId) => {
  const response = await authenticatedFetch(`${BASE_URL}/matches/${encodeURIComponent(matchId)}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to load match.');
  return data.match;
};

export const getMatchMessages = async (matchId) => {
  const response = await authenticatedFetch(`${BASE_URL}/matches/${encodeURIComponent(matchId)}/messages`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.messages || [];
};

// Ask a question about a match — 3 credits. Mirrors astroService.askQuestion's
// NO_CREDITS handling so the page can show the buy-credits modal.
export const askMatchQuestion = async (matchId, question) => {
  const response = await authenticatedFetch(`${BASE_URL}/matches/${encodeURIComponent(matchId)}/ask`, {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Failed to get a response.');
  if (data.error === 'NO_CREDITS' || data.error === 'MATCH_UNAVAILABLE') {
    const error = new Error(data.message);
    error.code = data.error;
    error.credits = data.credits;
    throw error;
  }
  return data;
};

// Public — invite landing page context (no auth; Person B may not have an account).
export const getInviteInfo = async (token) => {
  const response = await fetch(`${BASE_URL}/invite/${encodeURIComponent(token)}`);
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Invite not found.');
  return data;
};
